import React, { useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TextField,
  MenuItem,
  Tooltip,
  IconButton,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FunctionsIcon from '@mui/icons-material/Functions';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CloseIcon from '@mui/icons-material/Close';
import {
  ENGINEERED_FEATURE_TECHNIQUES,
  compatibleFieldsForTechnique,
  makeEngineeredFeature,
  recommendedEngineeredConfigs,
  engineeredKey,
} from './studioUtils';

/**
 * Engineered features for the studio.
 *
 * A compact summary section (chips of what's selected + an "Add" button)
 * backed by a full-catalog modal:
 *   - Time features  -> one-click toggle chips (source = the bucket timestamp)
 *   - Numeric techniques -> category + technique + source-field builder
 *   - Recommended techniques (from workspace analysis) are highlighted and
 *     one-click addable.
 *
 * Each config matches the backend contract: { technique, sourceField, name,
 * parameters? } (+ UI-only metadata the server ignores).
 */
const efLabel = (ef) => {
  if (ef.category === 'Time Features') return ef.techniqueName || ef.technique;
  const src = ef.sourceField && ef.sourceField !== 'ts' ? ` · ${ef.sourceField}` : '';
  return `${ef.techniqueName || ef.technique}${src}`;
};

const EngineeredFeatures = ({ fieldRows, fieldsData, value = [], onChange }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('all');
  const [technique, setTechnique] = useState('');
  const [sourceField, setSourceField] = useState('');

  const fineGrained = useMemo(
    () => ENGINEERED_FEATURE_TECHNIQUES.filter((t) => t.category !== 'Time Features'),
    [],
  );
  const timeFeatures = useMemo(
    () => ENGINEERED_FEATURE_TECHNIQUES.filter((t) => t.category === 'Time Features'),
    [],
  );
  const categories = useMemo(
    () => ['all', ...Array.from(new Set(fineGrained.map((t) => t.category)))],
    [fineGrained],
  );
  const filteredTech = useMemo(
    () => (category === 'all' ? fineGrained : fineGrained.filter((t) => t.category === category)),
    [category, fineGrained],
  );
  const compatFields = useMemo(
    () => (technique ? compatibleFieldsForTechnique(technique, fieldRows) : []),
    [technique, fieldRows],
  );

  const recommended = useMemo(
    () => recommendedEngineeredConfigs(fieldsData, fieldRows),
    [fieldsData, fieldRows],
  );
  const recTechniqueIds = useMemo(
    () => new Set(recommended.map((r) => r.technique)),
    [recommended],
  );

  const selectedKeys = useMemo(() => new Set(value.map(engineeredKey)), [value]);
  const has = (ef) => ef && selectedKeys.has(engineeredKey(ef));

  const add = (ef) => {
    if (ef && !has(ef)) onChange([...value, ef]);
  };
  const remove = (ef) =>
    onChange(value.filter((f) => engineeredKey(f) !== engineeredKey(ef)));
  const toggleTime = (techId) => {
    const ef = makeEngineeredFeature(techId, null, fieldRows);
    if (!ef) return;
    if (has(ef)) remove(ef);
    else add(ef);
  };
  const isTimeSelected = (techId) => value.some((f) => f.technique === techId);
  const addFineGrained = () => {
    if (!technique || !sourceField) return;
    add(makeEngineeredFeature(technique, sourceField, fieldRows));
    setSourceField('');
  };

  const recNotYetAdded = recommended.filter((r) => !has(r));

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <FunctionsIcon fontSize="small" sx={{ color: 'primary.main' }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Engineered features
          </Typography>
          {value.length > 0 && (
            <Chip
              size="small"
              label={value.length}
              sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.16) }}
            />
          )}
        </Stack>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setOpen(true)} sx={{ textTransform: 'none' }}>
          Add
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Derived signals (lags, moving averages, calendar features…) computed for
        you and added as extra columns.
      </Typography>

      {value.length === 0 ? (
        <Typography variant="caption" color="text.disabled">
          None yet{recNotYetAdded.length > 0 ? ` — ${recNotYetAdded.length} recommended` : ''}.
        </Typography>
      ) : (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {value.map((ef) => (
            <Chip
              key={engineeredKey(ef)}
              size="small"
              label={efLabel(ef)}
              onDelete={() => remove(ef)}
              icon={ef.isRecommended ? <AutoAwesomeIcon sx={{ fontSize: 13 }} /> : undefined}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                fontWeight: 600,
              }}
            />
          ))}
        </Stack>
      )}

      {/* ---- Catalog modal ---- */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              <FunctionsIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h6">Engineered features</Typography>
            </Stack>
            <IconButton size="small" onClick={() => setOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {/* Recommended */}
          {recNotYetAdded.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                <AutoAwesomeIcon fontSize="small" sx={{ color: 'primary.main' }} />
                <Typography variant="subtitle2" fontWeight={700}>
                  Recommended by analysis
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                {recNotYetAdded.map((ef) => (
                  <Chip
                    key={engineeredKey(ef)}
                    size="small"
                    label={efLabel(ef)}
                    onClick={() => add(ef)}
                    variant="outlined"
                    icon={<AddIcon sx={{ fontSize: 14 }} />}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Stack>
              <Divider sx={{ mt: 2 }} />
            </Box>
          )}

          {/* Build a numeric feature */}
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Build a feature
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
            <TextField
              select
              size="small"
              label="Category"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setTechnique('');
                setSourceField('');
              }}
              sx={{ minWidth: 150 }}
            >
              {categories.map((c) => (
                <MenuItem key={c} value={c}>
                  {c === 'all' ? 'All categories' : c}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Technique"
              value={technique}
              onChange={(e) => {
                setTechnique(e.target.value);
                setSourceField('');
              }}
              sx={{ minWidth: 190, flex: 1 }}
            >
              {filteredTech.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                  {recTechniqueIds.has(t.id) ? '  ★' : ''}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Source signal"
              value={sourceField}
              onChange={(e) => setSourceField(e.target.value)}
              disabled={!technique}
              sx={{ minWidth: 170, flex: 1 }}
              helperText={technique && compatFields.length === 0 ? 'No compatible signals' : ' '}
            >
              {compatFields.map((f) => (
                <MenuItem key={f.key} value={f.key}>
                  {f.display_name || f.original_key || f.key}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              size="small"
              onClick={addFineGrained}
              disabled={!technique || !sourceField}
              startIcon={<AddIcon />}
              sx={{ mt: { sm: 0.5 } }}
            >
              Add
            </Button>
          </Stack>
          {technique && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {ENGINEERED_FEATURE_TECHNIQUES.find((t) => t.id === technique)?.description}
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Time features */}
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
            <ScheduleIcon fontSize="small" sx={{ color: 'primary.main' }} />
            <Typography variant="subtitle2" fontWeight={700}>
              Calendar / time features
            </Typography>
          </Stack>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {timeFeatures.map((t) => {
              const selected = isTimeSelected(t.id);
              return (
                <Tooltip key={t.id} title={t.description}>
                  <Chip
                    size="small"
                    label={t.name}
                    onClick={() => toggleTime(t.id)}
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                  />
                </Tooltip>
              );
            })}
          </Stack>

          {/* Selected */}
          {value.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Selected ({value.length})
              </Typography>
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                {value.map((ef) => (
                  <Chip
                    key={engineeredKey(ef)}
                    size="small"
                    label={efLabel(ef)}
                    onDelete={() => remove(ef)}
                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12) }}
                  />
                ))}
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EngineeredFeatures;
